/**
 * Thanos.js
 * The Thanos Enemy class
 */

import { Game } from "pixi_core";
import { EnemyAbilities, GameEvents } from "../Constants";
import { Enemy } from "./Enemy";
import { EnemyParticle } from "../game/particles/EnemyParticle";
import { Common } from "../game/Common";

export class Thanos extends Enemy {
    /**
     * @public
     * @param {String} enemyName
     * @param {Object} enemyData
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     */
    constructor(enemyName, enemyData, index, summoned) {
        super(enemyName, enemyData, index, summoned);

        const probDoubleEdgedSword = Game.utils.assertNumber(enemyData.ProbDoubleEdgedSword, 1);
        const probCrushingPower = Game.utils.assertNumber(enemyData.ProbCrushingPower, 1);
        const probSpaceManipulation = Game.utils.assertNumber(enemyData.ProbSpaceManipulation, 1);

        /** @private @type {Number} */
        this._probRealityShift = Game.utils.assertNumber(enemyData.ProbRealityShift, 1);
        /** @private @type {Number} */
        this._probMindManipulation = Game.utils.assertNumber(enemyData.ProbMindManipulation, 1);

        /** @private @type {Number} - Amount of HP regenerated by the Regeneration ability */
        this._regenHP = (Game.utils.assertNumber(enemyData.RegenHealthPercent, 1) * this._maxHp) / 100;

        /** @private @type {Boolean} */
        this._thereIsNoTime = false;

        /** @private @type {Number} */
        this._snapStunNbr = Game.utils.assertNumber(enemyData.SnapStunNbr, 1);
        /** @private @type {Number} */
        this._snapStunTurns = Game.utils.assertNumber(enemyData.SnapStunTurns, 1);

        /** @private @type {Boolean} */
        this._isCharging = false;

        // Ability choices
        this._abilityChoices.addChoice(EnemyAbilities.eDOUBLE_EDGED_SWORD, probDoubleEdgedSword);
        this._abilityChoices.addChoice(EnemyAbilities.eCRUSHING_POWER, probCrushingPower);
        this._abilityChoices.addChoice(EnemyAbilities.eSPACE_MANIPULATION, probSpaceManipulation);
    }

    /**
     * Choose the next ability to use
     * @protected
     * @override
     * @returns {EnemyAbilities}
     */
    _chooseAbility() {
        return this._abilityChoices.choose();
    }

    /**
     * @protected
     * @override
     * @param {EnemyAbilities} abilityName
     * @param {{x: Number, y: Number}} startPos
     */
    _useAbility(abilityName, startPos) {
        switch (abilityName) {
            case EnemyAbilities.eSNAP:
                Game.fire(
                    GameEvents.eGAME_ABILITY_SNAP,
                    this._snapStunNbr,
                    this._snapStunTurns,
                    startPos,
                    this._onAbilityCompleted.bind(this)
                );
                break;

            case EnemyAbilities.eDOUBLE_EDGED_SWORD:
                Game.fire(
                    GameEvents.eGAME_ABILITY_DOUBLE_EDGED_SWORD,
                    this._attackDamage,
                    this._nbAddedLines,
                    startPos,
                    this._onAbilityCompleted.bind(this)
                );
                break;

            case EnemyAbilities.eCRUSHING_POWER:
                this._isCharging = true;
                Game.fire(GameEvents.eGAME_ABILITY_ENEMY_POWER_UP, this);
                this._continueCrushingPower();
                break;

            case EnemyAbilities.eSPACE_MANIPULATION:
                Game.fire(GameEvents.eGAME_ABILITY_ADD_OVERTIME, this._addOvertimeBubbles, null);
                this._continueSpaceManipulation(startPos);
                break;

            case EnemyAbilities.eREWIND:
                this._abilityChoices.addChoice(EnemyAbilities.eREALITY_SHIFT, this._probRealityShift);
                this._abilityChoices.addChoice(EnemyAbilities.eMIND_MANIPULATION, this._probMindManipulation);
                this._thereIsNoTime = true;
                Game.fire(GameEvents.eGAME_ABILITY_REGENERATION, this._regenHP, this._enemyName, this._onAbilityCompleted.bind(this));
                break;

            case EnemyAbilities.eREALITY_SHIFT:
                Game.fire(GameEvents.eGAME_ABILITY_REALITY_SHIFT, this, this._continueRealityShift.bind(this, startPos));
                break;

            case EnemyAbilities.eMIND_MANIPULATION:
                Game.fire(GameEvents.eGAME_ABILITY_JAM, this._stunNumber, this._stunTurns, startPos, this._onAbilityCompleted.bind(this));
                break;

            default:
                console.error("[Thanos._useAbility] Invalid ability name : " + abilityName);
                this._chooseNextAction();
        }
    }

    /**
     * @private
     */
    _continueCrushingPower() {
        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            Game.fire(
                GameEvents.eGAME_ABILITY_SHIELD_ENEMY,
                this._shieldNbr,
                this._shieldDuration,
                this._shieldPercent,
                this._onAbilityCompleted.bind(this)
            );
        };
    }

    /**
     * @private
     * @param {{x: Number, y: Number}} startPos
     */
    _continueSpaceManipulation(startPos) {
        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            Game.fire(GameEvents.eGAME_ABILITY_SPACE_MANIPULATION, startPos, this._onAbilityCompleted.bind(this));
        };
    }

    /**
     * @private
     * @param {{x: Number, y: Number}} startPos
     */
    _continueRealityShift(startPos) {
        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._timerMultipleAbilityDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.multipleAbilityDelay);
        this._timerMultipleAbilityDelay.onComplete = () => {
            Game.fire(GameEvents.eGAME_ABILITY_RANDOM_SHOOTER, this._randomShooterBubbles, startPos, this._onAbilityCompleted.bind(this));
        };
    }

    /**
     * The ability name is now hidden
     * @public
     * @override
     * @param {{x: Number, y: Number}} startPos
     */
    onAbilityNameCompleted(startPos) {
        if (this._preemptiveStrike) {
            this._chooseNextAction();
        } else {
            this._useAbility(this._selectedAbility, startPos);
        }
    }

    /**
     * Choose the next action in this enemy's attack phase
     * @protected
     * @override
     */
    _chooseNextAction() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn && this._hp > 0) {
            ++this._nbActionsPerformed;

            if (this._currentAttackTimer > 0 || this._isCharging) {
                this._regularAttack();
            } else {
                let ability = this._chooseAbility();
                if (this._preemptiveStrike) {
                    this._preemptiveStrike = false;
                    ability = EnemyAbilities.eSNAP;
                } else if (!this._thereIsNoTime && this._hp < (this._maxHp * this._triggerHealthPercent) / 100) {
                    ability = EnemyAbilities.eREWIND;
                } else {
                    ability = this._chooseAbility();
                }
                this._beginAbility(ability);
            }
        } else {
            this._onAttackPhaseCompleted();
        }
    }

    /**
     * The attack particle has reached its target
     * @protected
     * @override
     * @param {EnemyParticle} particle
     */
    _onDamageParticleCompleted(particle) {
        if (this._isCharging) {
            const chargedDamage = this._attackDamage * (this._chargingDamageBonus / 100);
            Game.fire(GameEvents.eHEROES_HIT, chargedDamage, particle);
            Game.fire(GameEvents.eGAME_ABILITY_ENEMY_POWER_DOWN, this);
            this._isCharging = false;
        } else {
            Game.fire(GameEvents.eHEROES_HIT, this._attackDamage, particle);
        }

        this._chooseNextAction();
    }
}
